import { useTranslation } from 'react-i18next';
import { generatePath, useNavigate, useParams } from 'react-router-dom';
import { PencilSquareIcon } from '@heroicons/react/24/outline';

import { logError } from 'services/logging/logging';
import { WebEDIAnnotation } from 'services/webediErrors/types';
import { useWebEDIStore } from 'stores/webedi/webedi';
import { GenericError } from 'support/helpers/errors/errors';
import { TransactionsRoutes, TransactionState } from 'support/types';
import { Button } from 'components/Form/Button';

import { useIsPartnerIssueResolver } from '../useIsPartnerIssueResolver';

type Props = {
  viewMode: boolean;
  annotation: WebEDIAnnotation;
};

export const EditDocumentButton = ({ viewMode, annotation }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id: messageId } = useParams<{ id: string }>();
  const isPartnerIssueResolver = useIsPartnerIssueResolver();
  const isDelivered = useWebEDIStore((state) => state.messageState === TransactionState.success);

  if (!viewMode || !isPartnerIssueResolver || isDelivered) {
    return null;
  }

  const handleClick = () => {
    if (!messageId) {
      logError(new GenericError('messageId is undefined'));
      return;
    }

    navigate(generatePath(TransactionsRoutes.edit, { id: messageId }), {
      state: { focusOnField: { fieldPath: annotation.fieldPath } },
    });
  };

  return (
    <Button
      type="button"
      variant="secondary"
      size="small"
      fullWidth
      LeftIcon={PencilSquareIcon}
      onMouseDown={(e) => e.preventDefault()}
      onClick={handleClick}
      analyticsId="webedi:fix_issue"
    >
      {t('webedi:fieldAnnotation.editDocument')}
    </Button>
  );
};
